// `/admin/settings`, croqui `screens/admin/configuracoes.tsx`: o kill switch, o
// adquirente padrão e os parâmetros de split (WEB-124, sem croqui) à esquerda, o painel
// "Ambiente" à direita.
//
// `PUT /admin/v1/settings/payouts-paused` e `PUT .../default-acquirer` estão em
// `ADMIN_STEP_UP_ROUTES`: o `StepUpDialog` sobe de `api/client.ts` depois da confirmação.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useId, useState } from 'react';
import {
  ADMIN_ACQUIRERS_QUERY_KEY,
  acquirersQueryOptions,
  type DefaultAcquirerBody,
  putDefaultAcquirer,
} from '../api/queries/admin-acquirers.ts';
import {
  adminSystemKeys,
  adminsQueryOptions,
  type PayoutsPausedBody,
  payoutsPausedQueryOptions,
  setPayoutsPaused,
} from '../api/queries/admin-system.ts';
import { API_BASE_URL, APP_ENV, RELEASE } from '../app/config.ts';
import { ConfirmDialog } from '../components/app/confirm-dialog.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import { DefaultAcquirerPanel } from '../features/admin-system/default-acquirer.tsx';
import { isStepUpCancelled } from '../features/admin-system/model.ts';
import { PayoutsSwitch } from '../features/admin-system/payouts-switch.tsx';
import { defaultAcquirerStateQueryOptions } from '../features/admin-system/settings-queries.ts';
import { SplitSettingsPanel } from '../features/admin-system/split-settings.tsx';

/** O schema da API exige `reason` com no mínimo 3 caracteres nos dois sentidos. */
const MIN_REASON = 3;

function AdminSettings() {
  const queryClient = useQueryClient();
  const reasonId = useId();
  const payouts = useQuery(payoutsPausedQueryOptions());
  const acquirers = useQuery(acquirersQueryOptions());
  const defaultAcquirer = useQuery(defaultAcquirerStateQueryOptions());
  // Só para escrever "Nome (usr_…)" em "mudado por": `payouts-paused` devolve o id cru.
  const admins = useQuery(adminsQueryOptions());

  const [pending, setPending] = useState<boolean | undefined>(undefined);
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: (body: PayoutsPausedBody) => setPayoutsPaused(body),
    onSuccess: (next) => {
      setPending(undefined);
      setReason('');
      queryClient.setQueryData(adminSystemKeys.payoutsPaused, next);
      void queryClient.invalidateQueries({ queryKey: adminSystemKeys.settings });
    },
    onError: (error) => {
      if (isStepUpCancelled(error)) {
        setPending(undefined);
      }
    },
  });

  const saveAcquirer = async (body: DefaultAcquirerBody): Promise<void> => {
    await putDefaultAcquirer(body);
    await queryClient.invalidateQueries({ queryKey: [ADMIN_ACQUIRERS_QUERY_KEY] });
  };

  const changedBy = admins.data?.find((admin) => admin.id === payouts.data?.updated_by)?.name;

  return (
    <>
      <PageHeader
        title="Configurações"
        sub="Kill switch de saques, adquirente padrão e parâmetros de split"
        path="/admin/v1/settings/payouts-paused"
      />

      {/* Composição (WEB-133): o kill switch é o controle mais crítico do backoffice e ocupa
          a faixa de cima inteira; os dois parâmetros dividem a linha de baixo; "Ambiente" é
          leitura e fecha a tela numa linha discreta, sem peso de cartão. */}
      <div className="flex flex-col gap-4">
        <Panel
          title="Kill switch de saques"
          sub="Pausa o envio de saques ao parceiro; os pedidos continuam entrando"
        >
          {payouts.isPending ? (
            <CardSkeleton lines={3} />
          ) : payouts.data === undefined ? (
            <ErrorState
              error={payouts.error}
              onRetry={() => {
                void payouts.refetch();
              }}
            />
          ) : (
            <PayoutsSwitch
              state={payouts.data}
              {...(changedBy === undefined ? {} : { adminName: changedBy })}
              isPending={save.isPending}
              onToggle={(next) => {
                save.reset();
                setReason('');
                setPending(next);
              }}
            />
          )}
        </Panel>

        <div className="grid items-start gap-4 lg:grid-cols-2">
          <Panel title="Adquirente padrão" sub="Usado quando um seller não tem override">
            <DefaultAcquirerPanel
              state={defaultAcquirer.data}
              acquirers={acquirers.data ?? []}
              {...(acquirers.error === null ? {} : { acquirersError: acquirers.error })}
              onSave={saveAcquirer}
            />
          </Panel>

          <Panel title="Split" sub="Teto por cobrança e cota padrão de 30 dias por seller">
            <SplitSettingsPanel />
          </Panel>
        </div>

        {/* Metadado de BUILD DO PAINEL (`app/config.ts`). Não há rota admin que devolva
            versão da API nem região do deploy (marca #145). */}
        <section aria-label="Ambiente" className="border-t border-border px-1 pt-4">
          <p className="micro-label">Ambiente · somente leitura</p>
          <dl className="mt-2 flex flex-wrap gap-x-8 gap-y-2 text-12">
            {[
              { k: 'versão do painel', v: RELEASE },
              { k: 'ambiente', v: APP_ENV },
              { k: 'API', v: API_BASE_URL },
            ].map((row) => (
              <div key={row.k} className="flex min-w-0 items-baseline gap-2">
                <dt className="shrink-0 text-muted-foreground">{row.k}</dt>
                <dd className="m-0 data-value min-w-0 break-all text-foreground">{row.v}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <ConfirmDialog
        open={pending !== undefined}
        title={pending === true ? 'Pausar todos os saques?' : 'Retomar os saques?'}
        description={
          pending === true
            ? 'Nenhum saque sai enquanto estiver ligado. Os pedidos continuam entrando na fila.'
            : 'Os saques aprovados voltam a ser enviados ao parceiro na próxima rodada do worker.'
        }
        confirmLabel={pending === true ? 'Pausar saques' : 'Retomar saques'}
        destructive={pending === true}
        isPending={save.isPending}
        disabled={reason.trim().length < MIN_REASON}
        {...(save.error === null || isStepUpCancelled(save.error) ? {} : { error: save.error })}
        onConfirm={() => {
          if (pending !== undefined) {
            save.mutate({ paused: pending, reason: reason.trim() });
          }
        }}
        onOpenChange={(open) => {
          if (!open) {
            setPending(undefined);
          }
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor={reasonId}>Motivo (fica na trilha de auditoria)</Label>
          <Input
            id={reasonId}
            value={reason}
            placeholder="Ex.: parceiro fora do ar"
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </div>
      </ConfirmDialog>
    </>
  );
}

export const Route = createFileRoute('/admin/settings/')({ component: AdminSettings });
